import React from 'react';

import { observer } from 'mobx-react';

import { reverse } from 'named-urls';

import Record from 'ui/Record/Record';

import { CenteredCircularProgress } from 'components/Progress';
import { Button } from 'components/Button';
import { MainHeader } from 'components/MainHeader/MainHeader';

import { formatMoment } from 'utils/formatters/timezone';

import NotFound from 'containers/Error/Pages/NotFound/NotFound';

import routes from 'core/routes';

import { useNavBarHeight } from 'utils/hooks/navbar';

import { useStore } from 'utils/hooks/store';
import useLayoutEditorParams from 'utils/hooks/useLayoutEditorParams';

import NavigationBlocker from 'components/NavigationBlocker';

import { settingsObjectLabel } from 'containers/Dashboard/components/SideBar/utils';
import { DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR, EntityModules } from 'core/constants';

import MultipleContainers from './components/Layout/MultipleContainer';

import { LastPublished } from './styles';

const LayoutEditor: React.FunctionComponent = (): React.ReactElement => {
  const { entityLayout, entityFields, routing, notification, session } = useStore();

  const navBarHeight = useNavBarHeight();
  const [saving, setSaving] = React.useState(false);

  const moduleName = useLayoutEditorParams();

  const onSave = async (): Promise<void> => {
    setSaving(true);
    await entityLayout.saveLayout(moduleName);
    setSaving(false);
  };

  const onCancel = async (): Promise<void> => {
    // Temporarily disable navigation blocker by setting contentOfLayoutWasEdited to false
    entityLayout.contentOfLayoutWasEdited = false;
    routing.push(routes.dashboard.home);
    notification.enqueueWarningSnackbar("Layout changes discarded. You've been returned to the Home screen.");
  };

  React.useEffect(() => {
    const fetchData = async (): Promise<void> => {
      await entityFields.loadFields(moduleName);
      await entityLayout.loadLayout(moduleName);
    };

    fetchData();

    return () => {
      entityFields.reset();
      entityLayout.reset();
    };
  }, [moduleName]);

  if (entityFields.isLoaded === false || entityLayout.isLoaded === false) {
    return <CenteredCircularProgress aria-label="layout-editor-loading" />;
  }

  if (entityLayout.error) {
    return <NotFound />;
  }

  const link =
    moduleName === EntityModules.Events
      ? reverse(routes.dashboard.settings.objects.details.formBuilder.toString(), { moduleName })
      : reverse(routes.dashboard.settings.objects.details.fieldManager, { moduleName });

  return (
    <>
      <NavigationBlocker
        when={entityLayout.contentOfLayoutWasEdited}
        shouldBlockNavigation={() => entityLayout.contentOfLayoutWasEdited}
        navigateMessage="You have made updates in the layout, but you haven't saved the changes. Do you want to discard these changes?"
      />

      <Record
        key={`layout-editor-${moduleName}`}
        disableMaxContentHeight
        Header={
          <MainHeader
            title="Layout Editor"
            breadcrumbs={[
              {
                label: 'Objects',
                link: '',
              },
              {
                label: settingsObjectLabel(moduleName as EntityModules, session.user),
                link,
              },
              {
                label: 'Layout Editor',
                link: reverse(routes.dashboard.settings.objects.details.layoutEditor, { moduleName }),
              },
            ]}
            action={
              <>
                <LastPublished data-testid="last-published">
                  {`Last published: ${formatMoment(entityLayout.lastPublished, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR)}`}
                </LastPublished>
                <Button
                  size="large"
                  variant="outlined"
                  onClick={onCancel}
                  style={{ margin: '4px 0' }}
                  disabled={!entityLayout.contentOfLayoutWasEdited}
                >
                  Cancel
                </Button>
                <Button
                  size="large"
                  onClick={onSave}
                  variant="contained"
                  style={{ marginLeft: '4px' }}
                  // disable button if there is no changes
                  disabled={!entityLayout.contentOfLayoutWasEdited || saving}
                >
                  Publish
                </Button>
              </>
            }
          />
        }
        Content={() => <MultipleContainers />}
        navBarHeight={navBarHeight}
      />
    </>
  );
};

export default observer(LayoutEditor);
